import { PageLifecycleState } from '@fullstory/client-api';
import { Constants } from '@fullstory/recording-api';

/**
 * Describes the latest user activity in the page
 */
export enum ActivityState {
  /** The page is definitely closed */
  Closed = 0,
  /** Machine is sleeping, page not known to be closed (but may be) */
  Sleeping = 1,
  /** Page is not focused */
  Unfocused = 2,
  /** Page is focused but not actively being used */
  Inactive = 3,
  /** Page is focused and actively being used */
  Active = 4,
}

export const NoHeartbeatThreshold =
  Constants.HeartbeatInterval + Constants.DefaultBundleUploadInterval;

interface ComputeActivityStateArgs {
  /**
   * Is the page closed?
   */
  closed: boolean;
  /**
   * Absolute time, in milliseconds, of the last event.
   */
  lastEventTime: number;
  /**
   * Absolute time, in milliseconds, of the last user action.
   */
  lastActiveTime: number;
  /**
   * Absolute time, in milliseconds, of the last heartbeat.
   */
  lastHeartbeatTime: number;
  /**
   * The last known lifecycle state of the page.
   */
  lastLifecycleState: PageLifecycleState | undefined;
  /**
   * The current time, in milliseconds.
   */
  now: number;
}

export const computeWebActivityState = ({
  closed,
  lastEventTime,
  lastActiveTime,
  lastHeartbeatTime,
  lastLifecycleState,
  now,
}: ComputeActivityStateArgs): ActivityState => {
  if (closed) {
    return ActivityState.Closed;
  }

  // There are no user events for a certain period of time.
  const pageActive = lastEventTime - lastActiveTime < Constants.InactivityThreshold;

  // We're still receiving the expected heartbeats. Note that user actions reset Heartbeats.
  const pageHeartBeating = now - Math.max(lastHeartbeatTime, lastActiveTime) < NoHeartbeatThreshold;

  // The page is still "alive", in the sense that user activity hasn't ceased for >30m.
  const pageAlive = now - lastEventTime < Constants.PageInactivityTimeout;

  const pageFocused = lastLifecycleState === PageLifecycleState.ACTIVE;

  if (!pageHeartBeating || !pageAlive) return ActivityState.Sleeping;
  else if (!pageFocused) return ActivityState.Unfocused;
  else if (!pageActive) return ActivityState.Inactive;
  return ActivityState.Active;
};
